import { meridiansEnum } from '../enums/meridians.enum';
import { TwelveHourModel } from './twelve-hour.model';
import { MinuteModel } from './minute.model';
import { SecondModel } from './second.model';
import { TwentyFourHourModel } from './twenty-four-hour.model';
import { timeFormatsEnum } from '../../../Core/Users/enums/time-formats.enum';
import moment from 'moment-timezone';

export class TimeModel {
    format: timeFormatsEnum;
    hour: TwelveHourModel | TwentyFourHourModel;
    minute: MinuteModel;
    second: SecondModel;
    meridian?: meridiansEnum;
    timeZone?: string;

    constructor(
        format: timeFormatsEnum = null,
        hour: TwelveHourModel | TwentyFourHourModel = null,
        minute: MinuteModel = null,
        meridian: meridiansEnum = null,
        timeZone: string = null,
        second: SecondModel = null,
    ) {
        if (format === timeFormatsEnum.twelveHour) {
            if (hour > 12) {
                hour = hour - 12 as TwelveHourModel;
            }
            if (hour === 0) {
                hour = 12 as TwelveHourModel;
            }
        }
        this.setFormat(format);
        this.setHour(hour);
        this.setMinute(minute);
        this.setSecond(second);
        this.setMeridian(meridian);
        this.setTimeZone(timeZone);
    }

    setFormat(value: timeFormatsEnum) {
        if (value === null) {
            this.format = timeFormatsEnum.twelveHour;
        } else {
            this.format = value;
        }
    }

    setHour(value: TwelveHourModel | TwentyFourHourModel) {
        this.hour = value;
    }

    setMinute(value: MinuteModel) {
        this.minute = value;
    }

    setSecond(value: SecondModel) {
        this.second = value;
    }

    setMeridian(value: meridiansEnum): void {
        if (this.format === timeFormatsEnum.twelveHour &&
            value === null
        ) {
            value = meridiansEnum.AM;
        } else if (this.format === timeFormatsEnum.twentyFourHour &&
            value !== null
        ) {
            value = null;
        }

        this.meridian = value;
    }

    setTimeZone(value: string) {
        this.timeZone = value;
    }

    /**
     * Compare this instance of the object with a provided similar object
     *
     * For the comparison of hours, this function allows for comparing times in 12H format and 24H format.
     *
     * @param comparisonTime
     */
    public equals(comparisonTime: TimeModel): boolean {
        // If the format is the same we can do a direct comparison
        let hoursValue: number;
        if (this.format === comparisonTime.format) {
            if (this.hour !== comparisonTime.hour) {
                return false;
            }
        } else if (this.format === 1) {
            hoursValue = this.convertFrom12HoursTo24Hours(this.hour);
            if (hoursValue !== comparisonTime.hour) {
                return false;
            }
        } else {
            hoursValue = this.convertFrom12HoursTo24Hours(comparisonTime.hour);
            if (this.hour !== hoursValue ) {
                return false;
            }
        }

        if (this.minute !== comparisonTime.minute) {
            return false;
        }

        if ((this.hasOwnProperty('meridian') &&
            !comparisonTime.hasOwnProperty('meridian')) ||
            (!this.hasOwnProperty('meridian') &&
            comparisonTime.hasOwnProperty('meridian'))
        ) {
            return false;
        }

        if (this.format === timeFormatsEnum.twelveHour &&
            this.meridian !== comparisonTime.meridian
        ) {
            return false;
        }

        if ((this.hasOwnProperty('timezone') &&
            !comparisonTime.hasOwnProperty('timezone')) ||
            (!this.hasOwnProperty('timezone') &&
                comparisonTime.hasOwnProperty('timezone'))
        ) {
            return false;
        }

        if (this.timeZone !== comparisonTime.timeZone) {
            return false;
        }

        return true;
    }

    /**
     * Output stored time information as a string using meridians
     */
    public asStringWithMeridians(): string {
        if (this.meridian === meridiansEnum.PM) {
            // The hour is in PM, which means noon needs to map to 12, all other hours get 12 added to them to become 24 hour time
            let hour;
            if (this.hour === 12) {
                hour = 12;
            } else {
                hour = this.hour + 12;
            }

            return moment()
                .tz(this.timeZone)
                .hours(hour)
                .minutes(this.minute)
                .format('hh:mm A z');
        } else if (this.meridian === meridiansEnum.AM) {
            // The hour is in the AM, which means midnight needs to map to 0, all other hours remain unchanged
            let hour;
            if (this.hour === 12) {
                hour = 0;
            } else {
                hour = this.hour;
            }

            return moment()
                .tz(this.timeZone)
                .hours(hour)
                .minutes(this.minute)
                .format('hh:mm A z');
        } else {
            return moment()
                .tz(this.timeZone)
                .hours(this.hour)
                .minutes(this.minute)
                .format('hh:mm A z');
        }
    }

    /**
     * Checks if time is a valid format
     * Format 1 - Ensure hour is between 1 and 12, minutes between 0 and 59
     * Format 2 - Ensure hour is between 0 and 23, minutes between 0 and 59
     */
    public isValidTime() {
        return !(this.hour === null ||
            (this.hour <= 0 && this.format === 1) ||
            (this.hour > 12 && this.format === 1) ||
            (this.hour > 23 && this.format === 2) ||
            (this.minute === null) ||
            (this.minute < 0) ||
            (this.minute > 59));
    }

    /**
     * Returns a new time object based on a provided time object in the requested format.
     */
    public changeFormat(newFormat: timeFormatsEnum): TimeModel {
        const oldFormat: timeFormatsEnum = this.format;
        const oldHour: TwelveHourModel | TwentyFourHourModel = !isNaN(this.hour) ? this.hour : 12;
        const oldMinute: MinuteModel = this.minute;

        let newHour: TwelveHourModel | TwentyFourHourModel;
        let newMeridian: meridiansEnum;

        // No change in the format. Make a clone of the object to return.
        if (newFormat === oldFormat) {
            return new TimeModel(
                oldFormat,
                oldHour,
                oldMinute,
                this.meridian,
                this.timeZone
            );
        }

        // Otherwise, change the format
        if (newFormat === timeFormatsEnum.twentyFourHour) {
            // 12 on a 12-hour clock is actually 0.
            newHour = oldHour === 12 ? 0 : oldHour;

            // If it's PM, 12 hours in the day have passed, so add 12.
            if (this.meridian === meridiansEnum.PM) {
                newHour = <TwentyFourHourModel>(newHour + 12);
            }

            newMeridian = null;
        } else if (newFormat === timeFormatsEnum.twelveHour) {
            newMeridian = oldHour < 12 ? meridiansEnum.AM : meridiansEnum.PM;
            let hour = (oldHour % 12);

            // 0 is 12 on a 12-hour clock.
            if (hour === 0) {
                hour = 12;
            }

            newHour = hour as TwelveHourModel;
        }

        const newTime: TimeModel = new TimeModel(
            newFormat,
            newHour,
            oldMinute,
            newMeridian,
            this.timeZone
        );

        return newTime;
    }

    /**
     * Convert an hour value in AM/PM format to it's 24 hour equivalent (0 to 23)
     *
     * @param hours
     */
    private convertFrom12HoursTo24Hours(hours: number) {
        let convertedHours: number;
        if (this.meridian === meridiansEnum.AM && hours < 11 ||
            this.meridian === meridiansEnum.PM && hours === 12
        ) {
            convertedHours = hours;
        } else {
            convertedHours = hours + 12;
            convertedHours = convertedHours === 24 ? 0 : convertedHours;
        }

        return convertedHours;
    }
}
